import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const HERO_POSTER = "/images/hero-poster.webp";

export default function Hero() {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 800], [0, 220]);
  const opacityBg = useTransform(scrollY, [0, 600], [1, 0.25]);
  const [time, setTime] = useState("");

  useEffect(() => {
    const upd = () => {
      const d = new Date();
      const opts = { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/Vienna" };
      setTime(d.toLocaleTimeString("de-AT", opts) + " CET");
    };
    upd();
    const i = setInterval(upd, 30000);
    return () => clearInterval(i);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[var(--rf-bg)]"
    >
      {/* Background video */}
      <motion.div
        style={{ y: yBg, opacity: opacityBg }}
        className="absolute inset-0 z-0"
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={HERO_POSTER}
          className="object-cover w-full h-full absolute inset-0"
          aria-hidden="true"
        >
          <source src="/videos/hero.webm" type="video/webm" />
          <source src="/videos/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/30" />
      </motion.div>

      {/* Decorative spinning ring */}
      <div className="hidden md:block absolute right-[-180px] top-[12%] z-10 ring-spin">
        <svg width="520" height="520" viewBox="0 0 520 520" fill="none">
          <circle cx="260" cy="260" r="258" stroke="rgba(255,255,255,0.05)" />
          <circle cx="260" cy="260" r="200" stroke="rgba(255,255,255,0.06)" />
          <circle cx="260" cy="260" r="140" stroke="rgba(255,26,26,0.18)" strokeDasharray="2 8" />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-20 max-w-[1600px] mx-auto px-6 md:px-10 pt-44 pb-28 min-h-screen flex flex-col justify-between">
        {/* Top eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center gap-3 font-mono-tag text-white/60">
            <span className="dot-pulse" />
            {t.hero.eyebrow}
          </div>
          <div className="hidden md:block font-mono-tag text-white/50">
            LUSTENAU · {time}
          </div>
        </motion.div>

        {/* Title */}
        <div className="mt-16 md:mt-24">
          <div className="grid md:grid-cols-12 items-end gap-6">
            <div className="md:col-span-9">
              <motion.h1
                className="font-display text-fluid-hero leading-[0.85] uppercase tracking-tighter"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.2 }}
              >
                <motion.span
                  className="block text-metallic"
                  initial={{ y: 60, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.15 }}
                >
                  {t.hero.title_top}
                </motion.span>
                <motion.span
                  className="block text-outline pl-[6vw]"
                  initial={{ y: 60, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.35 }}
                >
                  {t.hero.title_mid}
                </motion.span>
                <motion.span
                  className="block text-metallic"
                  initial={{ y: 60, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.55 }}
                >
                  {t.hero.title_bot}
                  <span className="text-[var(--rf-red)]">.</span>
                </motion.span>
              </motion.h1>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="md:col-span-3 max-w-md"
            >
              <p className="text-white/70 text-sm md:text-base leading-relaxed mb-8">
                {t.hero.subtitle}
              </p>

              <div className="flex flex-col gap-3">
                <button
                  data-testid="hero-cta-primary"
                  onClick={() => scrollTo("contact")}
                  className="group inline-flex items-center justify-between gap-3 px-6 py-4 btn-glow font-mono-tag w-full"
                >
                  {t.hero.cta_primary}
                  <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </button>

                <button
                  data-testid="hero-cta-secondary"
                  onClick={() => scrollTo("services")}
                  className="group inline-flex items-center justify-between gap-3 px-6 py-4 border border-white/15 hover:border-white/40 font-mono-tag text-white/80 hover:text-white transition-all w-full"
                >
                  {t.hero.cta_secondary}
                  <ArrowDown size={16} className="transition-transform group-hover:translate-y-1" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-16 md:mt-24 grid md:grid-cols-12 gap-8 items-end"
        >
          <div className="md:col-span-6 flex items-end gap-10">
            <Stat value={t.hero.stat1_value} label={t.hero.stat1_label} />
            <div className="w-px h-16 bg-white/10" />
            <Stat value={t.hero.stat2_value} label={t.hero.stat2_label} />
            <div className="w-px h-16 bg-white/10" />
            <Stat value={t.hero.stat3_value} label={t.hero.stat3_label} />
          </div>

          <div className="md:col-span-6 flex items-center justify-end gap-3 font-mono-tag text-white/60">
            <span className="arrow-bounce inline-flex">
              <ArrowDown size={14} />
            </span>
            {t.hero.scroll}
          </div>
        </motion.div>
      </div>

      {/* Loader bar at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-white/5 z-20">
        <div className="loader-bar h-px bg-[var(--rf-red)] w-full" />
      </div>
    </section>
  );
}

const Stat = ({ value, label }) => (
  <div>
    <div className="font-display text-4xl md:text-5xl text-metallic leading-none">{value}</div>
    <div className="font-mono-tag text-white/50 mt-2">{label}</div>
  </div>
);
